import {
  Alert,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertTriangle,
  Archive,
  ArrowRightLeft,
  Boxes,
  Minus,
  Pencil,
  Plus,
  ScanLine,
} from 'lucide-react'
import { useState } from 'react'

import { errorMessage } from '@/api/errors'
import {
  useCurrentPermissions,
  useInventoryItems,
  useInventoryMovements,
  useInventoryMutations,
  useLocations,
  useProducts,
  useSuppliers,
} from '@/api/queries'
import type { InventoryItem, Uuid } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { formatDateTime, formatMoney, formatNumber } from '@/lib/format'
import { notify } from '@/lib/toast'

/**
 * Il magazzino — F8, INV-05.
 *
 * ## Una tabella, e la giacenza è la colonna che si guarda
 *
 * Chi apre questa pagina ha una domanda sola: **cosa manca**. Per questo il filtro «solo sotto
 * scorta» è un pulsante in cima e non una voce in un menù, e la riga porta la giacenza per sede
 * accanto al totale: «dodici» su tre sedi non dice se puoi vendere qui.
 *
 * ## I gesti sono quattro e stanno sulla riga
 *
 * Carico, scarico, rettifica e trasferimento si fanno **dove si legge la quantità**, non in una pagina
 * di dettaglio: chi scarica una bolla ha lo scatolone in mano e non naviga. Il modulo si apre sotto la
 * riga e chiede solo ciò che serve a quel gesto — il motivo su scarico e rettifica, la sede d'arrivo
 * sul trasferimento.
 *
 * ## Una giacenza negativa si **mostra**, non si nasconde
 *
 * È il dato che dice «il magazzino non è aggiornato» (INV-01), e una schermata che la mostrasse come
 * zero renderebbe invisibile l'unico segnale che qualcuno deve vedere. Sta lì, in rosso, con la sua
 * riga di registro dietro.
 *
 * ## Il registro si apre, non si riassume
 *
 * Sotto ogni articolo c'è la sua storia — «chi, quando, quanto, perché» —, che è la risposta alla
 * domanda per cui INV-02 esiste. Caricarla per ogni riga sarebbero duecento richieste per una tabella
 * di duecento articoli: si carica quando si apre.
 */
export const Route = createFileRoute('/_app/magazzino/')({
  component: WarehousePage,
})

type Gesture = 'load' | 'unload' | 'adjust' | 'transfer'

const GESTURE_LABELS: Record<Gesture, string> = {
  load: 'Carico',
  unload: 'Scarico',
  adjust: 'Inventario',
  transfer: 'Trasferimento',
}

const MOVEMENT_LABELS: Record<string, string> = {
  load: 'Carico',
  unload: 'Scarico',
  adjust: 'Rettifica',
  transfer_in: 'Arrivato',
  transfer_out: 'Partito',
  sale: 'Vendita',
}

function WarehousePage() {
  const permissions = useCurrentPermissions()
  const canRead = permissions.has('product:read')
  const canWrite = permissions.has('product:write')
  const [lowStock, setLowStock] = useState(false)
  const [locationId, setLocationId] = useState<Uuid | null>(null)
  const items = useInventoryItems({ lowStock, locationId })
  const locations = useLocations()
  const rows = items.data?.data ?? []

  if (!canRead) {
    return (
      <>
        <PageHeader title="Magazzino" description="Cosa c'è, cosa manca, cosa si è mosso." />
        <EmptyState
          icon={<Boxes />}
          title="Non hai accesso al magazzino"
          description="Il magazzino richiede il permesso «product:read», lo stesso del catalogo."
        />
      </>
    )
  }

  return (
    <>
      <PageHeader title="Magazzino" description="Cosa c'è, cosa manca, cosa si è mosso." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          variant={lowStock ? 'primary' : 'outline'}
          size="sm"
          onClick={() => {
            setLowStock((value) => !value)
          }}
        >
          <AlertTriangle className="size-4" /> Solo sotto scorta
        </Button>
        <select
          value={locationId ?? ''}
          onChange={(event) => {
            setLocationId(event.target.value === '' ? null : event.target.value)
          }}
          className="border-border rounded-md border px-2 py-1 text-sm"
        >
          <option value="">Tutte le sedi</option>
          {(locations.data ?? []).map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
        {canWrite ? <AddItem /> : null}
      </div>

      {items.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : items.isError && items.data === undefined ? (
        <ErrorState
          message={errorMessage(items.error)}
          onRetry={() => {
            void items.refetch()
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Boxes />}
          title={lowStock ? 'Niente sotto scorta' : 'Magazzino vuoto'}
          description={
            lowStock
              ? 'Nessun articolo è sotto la sua scorta minima. Togli il filtro per vedere tutto.'
              : 'Porta in magazzino un prodotto del catalogo: solo la merce ha una giacenza.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {rows.map((item) => (
            <ItemRow key={item.id} item={item} canWrite={canWrite} />
          ))}
        </ul>
      )}

      <SuppliersPanel canWrite={canWrite} />
    </>
  )
}

/**
 * I fornitori — INV-01.
 *
 * Un elenco piatto e senza paginazione, e sta in fondo alla pagina di proposito: non è
 * un'anagrafica da gestire, è il vocabolario con cui si dice «questa merce arriva da
 * qui». Serve nel momento in cui si corregge un articolo, ed è lì che compare come
 * scelta — qui si aggiunge il nome che nella tendina non c'era.
 *
 * Nessuna modifica e nessuna cancellazione, perché il contract non le ha: un fornitore
 * sbagliato si smette di usare, e le righe di magazzino che lo nominano restano
 * leggibili. È la stessa ragione per cui gli articoli si archiviano.
 */
function SuppliersPanel({ canWrite }: { canWrite: boolean }) {
  const suppliers = useSuppliers()
  const { addSupplier } = useInventoryMutations()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const rows = suppliers.data?.data ?? []

  return (
    <section className="border-border mt-6 rounded-lg border p-4">
      <h2 className="typo-label-lg mb-1 font-semibold">Fornitori</h2>
      <p className="text-muted-foreground mb-3 text-xs">
        Da chi arriva la merce. Si scelgono correggendo un articolo.
      </p>

      {suppliers.isPending ? (
        <Skeleton className="h-10 w-full" />
      ) : suppliers.isError ? (
        <Alert variant="danger">{errorMessage(suppliers.error)}</Alert>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nessun fornitore.</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {rows.map((supplier) => (
            <li
              key={supplier.id}
              className="border-border rounded-md border px-2 py-1 text-sm"
              title={[supplier.email, supplier.phone].filter((v) => v !== null).join(' · ')}
            >
              {supplier.name}
            </li>
          ))}
        </ul>
      )}

      {canWrite ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-[2fr_2fr_1fr_auto]">
          <Field label="Nome">
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Email">
            {(props) => (
              <Input
                {...props}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Field label="Telefono">
            {(props) => (
              <Input {...props} value={phone} onChange={(e) => setPhone(e.target.value)} />
            )}
          </Field>
          <div className="flex items-end">
            <Button
              size="sm"
              disabled={name.trim() === ''}
              loading={addSupplier.isPending}
              onClick={() =>
                addSupplier.mutate(
                  {
                    name: name.trim(),
                    ...(email.trim() === '' ? {} : { email: email.trim() }),
                    ...(phone.trim() === '' ? {} : { phone: phone.trim() }),
                  },
                  {
                    onSuccess: () => {
                      setName('')
                      setEmail('')
                      setPhone('')
                    },
                  },
                )
              }
            >
              <Plus className="size-4" />
              Aggiungi
            </Button>
          </div>
        </div>
      ) : null}

      {addSupplier.isError ? (
        <Alert variant="danger" className="mt-2">
          {errorMessage(addSupplier.error)}
        </Alert>
      ) : null}
    </section>
  )
}

/** Porta in magazzino un prodotto del catalogo: **non** crea un prodotto nuovo. */
function AddItem() {
  const products = useProducts({ kind: 'goods' })
  const mutations = useInventoryMutations()
  const [open, setOpen] = useState(false)
  const [productId, setProductId] = useState('')
  const [minStock, setMinStock] = useState('0')
  const [barcode, setBarcode] = useState('')

  if (!open) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setOpen(true)
        }}
      >
        <Plus className="size-4" /> Porta in magazzino
      </Button>
    )
  }

  return (
    <div className="border-border flex w-full flex-wrap items-end gap-2 rounded-lg border p-3">
      <label className="text-xs">
        Prodotto
        <select
          value={productId}
          onChange={(event) => setProductId(event.target.value)}
          className="border-border mt-1 block rounded-md border px-2 py-1 text-sm"
        >
          <option value="">Scegli…</option>
          {(products.data?.data ?? []).map((product) => (
            <option key={product.id} value={product.id}>
              {product.name}
            </option>
          ))}
        </select>
        {/* Solo `goods`: un abbonamento non ha giacenza, e dargliene una vorrebbe dire poterlo
            esaurire. L'elenco è già filtrato, così il rifiuto del server non arriva mai. */}
        <span className="text-muted-foreground mt-1 block">Solo merce: gli abbonamenti no.</span>
      </label>
      <label className="text-xs">
        Scorta minima
        <input
          type="number"
          min={0}
          value={minStock}
          onChange={(event) => setMinStock(event.target.value)}
          className="border-border mt-1 block w-24 rounded-md border px-2 py-1 text-sm"
        />
      </label>
      <label className="text-xs">
        Codice a barre
        <input
          value={barcode}
          onChange={(event) => setBarcode(event.target.value)}
          placeholder="8001234567890"
          className="border-border mt-1 block rounded-md border px-2 py-1 text-sm"
        />
      </label>
      <Button
        size="sm"
        disabled={productId === '' || mutations.addItem.isPending}
        onClick={() => {
          void (async () => {
            try {
              await mutations.addItem.mutateAsync({
                productId,
                minStock: Number(minStock) || 0,
                ...(barcode.trim() === '' ? {} : { barcode: barcode.trim() }),
              })
              setOpen(false)
              setProductId('')
              setBarcode('')
            } catch (error) {
              notify({ message: errorMessage(error), variant: 'danger' })
            }
          })()
        }}
      >
        Aggiungi
      </Button>
    </div>
  )
}

function ItemRow({ item, canWrite }: { item: InventoryItem; canWrite: boolean }) {
  const [gesture, setGesture] = useState<Gesture | null>(null)
  const [openRegister, setOpenRegister] = useState(false)
  const [editing, setEditing] = useState(false)
  const movements = useInventoryMovements(openRegister ? item.id : null)
  const mutations = useInventoryMutations()

  return (
    <li className="border-border rounded-lg border p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-medium">{item.productName}</span>
        {item.barcode === null ? null : (
          <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
            <ScanLine className="size-3" /> {item.barcode}
          </span>
        )}
        {item.supplierName === null ? null : (
          <span className="text-muted-foreground text-xs">{item.supplierName}</span>
        )}
        <span className="ml-auto flex items-center gap-2">
          {/* Una giacenza negativa si **mostra**: è il dato che dice «il magazzino non è
              aggiornato», e mostrarla come zero renderebbe invisibile l'unico segnale utile. */}
          <span
            className={`text-lg font-semibold tabular-nums ${
              item.totalStock < 0 ? 'text-danger-content' : ''
            }`}
          >
            {formatNumber(item.totalStock)}
          </span>
          {item.belowMinStock ? <Badge variant="warning">sotto scorta</Badge> : null}
        </span>
      </div>

      <div className="text-muted-foreground mt-1 flex flex-wrap gap-3 text-xs">
        <span>minimo {formatNumber(item.minStock)}</span>
        {item.purchasePriceCents === null ? null : (
          <span>acquisto {formatMoney(item.purchasePriceCents)}</span>
        )}
        {/* La giacenza per sede accanto al totale: «dodici» su tre sedi non dice se puoi vendere qui. */}
        {item.stock.map((entry) => (
          <span key={entry.locationId}>
            {entry.locationName}: <span className="tabular-nums">{entry.quantity}</span>
          </span>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        {canWrite
          ? (['load', 'unload', 'adjust', 'transfer'] as Gesture[]).map((kind) => (
              <Button
                key={kind}
                variant={gesture === kind ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setGesture(gesture === kind ? null : kind)
                }}
              >
                {kind === 'load' ? <Plus className="size-4" /> : null}
                {kind === 'unload' ? <Minus className="size-4" /> : null}
                {kind === 'transfer' ? <ArrowRightLeft className="size-4" /> : null}
                {GESTURE_LABELS[kind]}
              </Button>
            ))
          : null}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpenRegister((value) => !value)
          }}
        >
          Registro
        </Button>
        {canWrite ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing((value) => !value)
              }}
            >
              <Pencil className="size-4" /> Correggi
            </Button>
            {/* Archiviare **non** toglie dal listino: il prodotto resta vendibile, e
                sparisce solo la giacenza. La differenza conta perché la merce da banco
                e la merce in magazzino sono lo stesso prodotto visto da due lati, e
                chi archivia si aspetta di smettere di contarla, non di venderla. */}
            <Button
              variant="ghost"
              size="sm"
              loading={mutations.archiveItem.isPending}
              onClick={() => {
                void (async () => {
                  try {
                    await mutations.archiveItem.mutateAsync(item.id)
                    notify({
                      message: 'Articolo archiviato. Il prodotto resta in listino.',
                      variant: 'success',
                    })
                  } catch (error) {
                    notify({ message: errorMessage(error), variant: 'danger' })
                  }
                })()
              }}
            >
              <Archive className="size-4" /> Archivia
            </Button>
          </>
        ) : null}
      </div>

      {editing ? (
        <EditItemForm
          item={item}
          onDone={() => {
            setEditing(false)
          }}
        />
      ) : null}

      {gesture === null ? null : (
        <MovementForm
          item={item}
          gesture={gesture}
          onDone={() => {
            setGesture(null)
          }}
        />
      )}

      {openRegister ? (
        <div className="bg-muted/40 mt-2 rounded-md p-2 text-xs">
          {movements.isPending ? (
            <Skeleton className="h-10 w-full" />
          ) : (movements.data?.data ?? []).length === 0 ? (
            <Alert>Nessun movimento: questo articolo non si è ancora mosso.</Alert>
          ) : (
            <ul className="space-y-1">
              {(movements.data?.data ?? []).map((movement) => (
                <li key={movement.id} className="flex flex-wrap gap-2">
                  <span className="tabular-nums">{formatDateTime(movement.occurredAt)}</span>
                  <span>{MOVEMENT_LABELS[movement.kind] ?? movement.kind}</span>
                  <span className="font-medium tabular-nums">
                    {movement.delta > 0 ? `+${String(movement.delta)}` : movement.delta}
                  </span>
                  <span className="text-muted-foreground">
                    → {formatNumber(movement.quantityAfter)}
                  </span>
                  {movement.locationName === null ? null : <span>{movement.locationName}</span>}
                  {movement.countedQuantity === null ? null : (
                    <span>contati {formatNumber(movement.countedQuantity)}</span>
                  )}
                  {movement.reason === null ? null : (
                    <span className="text-muted-foreground italic">{movement.reason}</span>
                  )}
                  {movement.createdByName === null ? null : (
                    <span className="text-muted-foreground ml-auto">{movement.createdByName}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </li>
  )
}

/**
 * La correzione di un articolo — `PATCH /organization/inventory-items/{id}`.
 *
 * Corregge **come l'articolo è descritto**, non quanto ce n'è: la giacenza si muove solo
 * con un movimento, che lascia una riga nel registro. Una casella «quantità» qui
 * sarebbe il modo di cambiare il magazzino senza che risulti da nessuna parte.
 *
 * `null` e stringa vuota sono cose diverse, e i campi lo rispettano: svuotare il codice
 * a barre significa «non ne ha», non «lascia com'era».
 */
function EditItemForm({ item, onDone }: { item: InventoryItem; onDone: () => void }) {
  const mutations = useInventoryMutations()
  const suppliers = useSuppliers()
  const [barcode, setBarcode] = useState(item.barcode ?? '')
  const [category, setCategory] = useState(item.category ?? '')
  const [supplierId, setSupplierId] = useState(item.supplierId ?? '')
  const [minStock, setMinStock] = useState(String(item.minStock))
  const [purchase, setPurchase] = useState(
    item.purchasePriceCents === null ? '' : (item.purchasePriceCents / 100).toFixed(2),
  )

  const parsedPurchase = Number.parseFloat(purchase.replace(',', '.'))
  const purchaseInvalid = purchase.trim() !== '' && !Number.isFinite(parsedPurchase)

  return (
    <div className="border-border mt-2 grid gap-2 rounded-md border p-3 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]">
      <Field label="Codice a barre" hint="Vuoto: non ne ha.">
        {(props) => (
          <Input {...props} value={barcode} onChange={(e) => setBarcode(e.target.value)} />
        )}
      </Field>
      <Field label="Categoria">
        {(props) => (
          <Input {...props} value={category} onChange={(e) => setCategory(e.target.value)} />
        )}
      </Field>
      <Field label="Fornitore">
        {(props) => (
          <Select
            {...props}
            value={supplierId}
            onValueChange={setSupplierId}
            placeholder="Nessuno"
            items={[
              { value: '', label: 'Nessuno' },
              ...(suppliers.data?.data ?? []).map((supplier) => ({
                value: supplier.id,
                label: supplier.name,
              })),
            ]}
          />
        )}
      </Field>
      <Field label="Scorta minima">
        {(props) => (
          <Input
            {...props}
            type="number"
            min={0}
            value={minStock}
            onChange={(e) => setMinStock(e.target.value)}
          />
        )}
      </Field>
      <Field label="Prezzo d'acquisto" hint="In euro.">
        {(props) => (
          <Input {...props} value={purchase} onChange={(e) => setPurchase(e.target.value)} />
        )}
      </Field>
      <div className="flex items-end gap-2 sm:col-span-5">
        <Button
          size="sm"
          disabled={purchaseInvalid}
          loading={mutations.updateItem.isPending}
          onClick={() => {
            void (async () => {
              try {
                await mutations.updateItem.mutateAsync({
                  itemId: item.id,
                  input: {
                    barcode: barcode.trim() === '' ? null : barcode.trim(),
                    category: category.trim() === '' ? null : category.trim(),
                    supplierId: supplierId === '' ? null : supplierId,
                    minStock: Number.parseInt(minStock, 10) || 0,
                    // Centesimi interi, come ogni importo del contract: il valore
                    // digitato in euro si arrotonda **una volta** qui, e non
                    // sopravvive come decimale a nessun conto successivo.
                    purchasePriceCents:
                      purchase.trim() === '' ? null : Math.round(parsedPurchase * 100),
                  },
                })
                notify({ message: 'Articolo corretto.', variant: 'success' })
                onDone()
              } catch (error) {
                notify({ message: errorMessage(error), variant: 'danger' })
              }
            })()
          }}
        >
          Salva
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Annulla
        </Button>
      </div>
    </div>
  )
}

/**
 * Il modulo di un gesto: chiede **solo** ciò che serve a quel gesto.
 *
 * Un unico modulo con tutti i campi avrebbe mostrato «sede d'arrivo» su un carico e «quanto hai
 * contato» su uno scarico — cioè avrebbe insegnato a ignorare i campi.
 */
function MovementForm({
  item,
  gesture,
  onDone,
}: {
  item: InventoryItem
  gesture: Gesture
  onDone: () => void
}) {
  const locations = useLocations()
  const mutations = useInventoryMutations()
  const options = locations.data ?? []
  const [locationId, setLocationId] = useState<string>(
    item.stock[0]?.locationId ?? options[0]?.id ?? '',
  )
  const [toLocationId, setToLocationId] = useState<string>('')
  const [quantity, setQuantity] = useState('1')
  const [counted, setCounted] = useState('0')
  const [reason, setReason] = useState('')
  const needsReason = gesture === 'unload' || gesture === 'adjust'

  return (
    <div className="border-border mt-2 flex flex-wrap items-end gap-2 rounded-md border p-2 text-xs">
      <label>
        {gesture === 'transfer' ? 'Da' : 'Sede'}
        <select
          value={locationId}
          onChange={(event) => setLocationId(event.target.value)}
          className="border-border mt-1 block rounded-md border px-2 py-1"
        >
          {options.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
      </label>
      {gesture === 'transfer' ? (
        <label>
          A
          <select
            value={toLocationId}
            onChange={(event) => setToLocationId(event.target.value)}
            className="border-border mt-1 block rounded-md border px-2 py-1"
          >
            <option value="">Scegli…</option>
            {options
              .filter((location) => location.id !== locationId)
              .map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
          </select>
        </label>
      ) : null}
      {gesture === 'adjust' ? (
        <label>
          Quanti ne hai contati
          <input
            type="number"
            value={counted}
            onChange={(event) => setCounted(event.target.value)}
            className="border-border mt-1 block w-24 rounded-md border px-2 py-1"
          />
        </label>
      ) : (
        <label>
          Quantità
          <input
            type="number"
            min={1}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            className="border-border mt-1 block w-20 rounded-md border px-2 py-1"
          />
          {/* Sempre positiva: il verso lo dice il gesto, e un numero col segno avrebbe permesso un
              «carico di −5» — un'uscita travestita. */}
        </label>
      )}
      {needsReason ? (
        <label className="flex-1">
          Motivo
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={gesture === 'adjust' ? 'Inventario di agosto' : 'Rotte in magazzino'}
            className="border-border mt-1 block w-full rounded-md border px-2 py-1"
          />
        </label>
      ) : null}
      <Button
        size="sm"
        disabled={
          mutations.move.isPending ||
          locationId === '' ||
          (gesture === 'transfer' && toLocationId === '') ||
          (needsReason && reason.trim() === '')
        }
        onClick={() => {
          void (async () => {
            try {
              await mutations.move.mutateAsync({
                inventoryItemId: item.id,
                kind: gesture,
                locationId,
                ...(gesture === 'transfer' ? { toLocationId } : {}),
                ...(gesture === 'adjust'
                  ? { countedQuantity: Number(counted) || 0 }
                  : { quantity: Number(quantity) || 1 }),
                ...(needsReason ? { reason: reason.trim() } : {}),
              })
              notify({ message: 'Registrato.', variant: 'success' })
              onDone()
            } catch (error) {
              notify({ message: errorMessage(error), variant: 'danger' })
            }
          })()
        }}
      >
        Registra
      </Button>
    </div>
  )
}
